import { useEffect, useState } from 'react'
import { Panel, Button, Field, Chip, Banner, useToast } from '../../ds/index'
import { PasswordField } from '../../components/PasswordField'
import { useApi } from '../../lib/useApi'
import { post, patch } from '../../lib/api'
import { dateShort } from '../../lib/format'
import { useSession } from '../../lib/session'
import { roleLabel } from '../../components/ViewAs'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { PhoneAlerts } from '../../components/PhoneAlerts'
import { MIN_PASSWORD } from '../../lib/password'

type Account = { id: string; name: string; email: string; phone: string | null; role: 'admin' | 'rep' | 'spec'; created_at: string; sessions: number; password_changed_at: string | null }

export default function AccountSettings() {
  const { data, error, reload } = useApi<{ account: Account; viewing_as: boolean }>('/api/account')
  const { me, refresh } = useSession()
  const toast = useToast()
  const [profile, setProfile] = useState({ name: '', phone: '' })
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' })
  const [pwErr, setPwErr] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => { if (data) setProfile({ name: data.account.name, phone: data.account.phone || '' }) }, [data])
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const a = data.account
  const locked = data.viewing_as

  const saveProfile = async () => {
    setBusy('profile')
    try { await patch('/api/account', { name: profile.name, phone: profile.phone }); toast('Saved', 'pos'); reload(); refresh() } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(null) }
  }
  const changePassword = async () => {
    setPwErr(null)
    if (pw.next !== pw.confirm) { setPwErr('The two new passwords do not match'); return }
    setBusy('pw')
    try {
      const r = await post<{ other_sessions_ended: number }>('/api/account/password', { current_password: pw.current, new_password: pw.next })
      setPw({ current: '', next: '', confirm: '' })
      toast(r.other_sessions_ended ? `Password changed. Signed out ${r.other_sessions_ended} other device${r.other_sessions_ended === 1 ? '' : 's'}.` : 'Password changed', 'pos')
      reload()
    } catch (e) { setPwErr((e as Error).message) } finally { setBusy(null) }
  }
  const signOutOthers = async () => {
    setBusy('others')
    try { const r = await post<{ ended: number }>('/api/account/sign-out-others', {}); toast(r.ended ? `Signed out ${r.ended} other device${r.ended === 1 ? '' : 's'}` : 'No other devices were signed in', 'pos'); reload() } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(null) }
  }

  return (
    <>
      {locked && <div style={{ marginBottom: 'var(--s-6)' }}><Banner tone="warn" title={`This is ${a.name}'s account`}>You are viewing as them, so changes here are off.</Banner></div>}
      <div className="two-col">
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Profile</h2><Chip tone={a.role === 'spec' ? 'accent' : undefined}>{roleLabel(a.role, me?.org.name || '')}</Chip></div>
          <div className="stack-tight">
            <Field label="Name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} disabled={locked} />
            <Field label="Phone" type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} disabled={locked} />
            <div className="readonly-row"><span className="s-label muted">Sign-in email</span><span>{a.email}</span><span className="muted">To change it, ask an admin.</span></div>
            <div><Button onClick={saveProfile} disabled={locked || busy !== null || !profile.name.trim()}>{busy === 'profile' ? 'Saving' : 'Save profile'}</Button></div>
          </div>
        </Panel>

        <Panel>
          <div className="panel-header"><h2 className="panel-title">Password</h2><span className="muted">{a.password_changed_at ? `changed ${dateShort(a.password_changed_at)}` : 'never changed here'}</span></div>
          <form className="stack-tight" onSubmit={(e) => { e.preventDefault(); changePassword() }}>
            <input type="text" autoComplete="username" value={a.email} readOnly hidden />
            <PasswordField label="Current password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} disabled={locked} />
            <PasswordField label="New password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} disabled={locked} />
            <PasswordField label="New password again" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} disabled={locked} />
            <p className="form-help" style={{ margin: 0 }}>At least {MIN_PASSWORD} characters. Every other device you are signed in on is signed out.</p>
            {pwErr && <p className="form-error" role="alert">{pwErr}</p>}
            <div><Button type="submit" disabled={locked || busy !== null || !pw.current || pw.next.length < MIN_PASSWORD || !pw.confirm}>{busy === 'pw' ? 'Changing' : 'Change password'}</Button></div>
          </form>
        </Panel>
      </div>

      {(a.role === 'rep' || a.role === 'spec') && (
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Lead alerts on your phone</h2></div>
          <PhoneAlerts />
        </Panel>
      )}
      <div style={{ height: 'var(--s-6)' }} />
      <Panel>
        <div className="panel-header"><h2 className="panel-title">Where you are signed in</h2></div>
        <div className="spread wrap-gap">
          <span>{a.sessions === 1 ? 'Only this device.' : `${a.sessions} devices, including this one.`} <span className="muted">Member since {dateShort(a.created_at)}.</span></span>
          <Button variant="secondary" onClick={signOutOthers} disabled={locked || busy !== null || a.sessions <= 1}>{busy === 'others' ? 'Signing out' : 'Sign out everywhere else'}</Button>
        </div>
      </Panel>
    </>
  )
}
